import SendEventOnLoad from '$store/components/SendEventOnLoad.tsx'
import Description from '$store/components/product/Description.tsx'
import PaymentMethods from '$store/components/product/PaymentMethods.tsx'
import KitLookDetails from '$store/components/product/kit-look/KitLookDetails.tsx'
import PDPTimer from '$store/components/timer/PDPTimer.tsx'
import Icon from '$store/components/ui/Icon.tsx'
import ProductDetailsAddToCart from '$store/islands/ProductDetailsAddToCart.tsx'
import ProductDetailsVariantSelector from '$store/islands/ProductDetailsVariantSelector.tsx'
import { SealConfig } from '$store/loaders/seals.tsx'
import { TimerConfig } from '$store/loaders/timers.tsx'
import { formatPrice } from '$store/sdk/format.ts'
import { useOffer } from '$store/sdk/useOffer.ts'
import { useSeals } from '$store/sdk/useSeals.ts'
import { SimilarProduct } from '$store/sdk/useSimilars.ts'
import { getCheapestVariantOffers } from '$store/utils/getCheapestVariantOffers.ts'
import { mapProductToAnalyticsItem } from '$store/utils/productToAnalyticsItem.ts'
import { signal } from '@preact/signals'
import type { ProductDetailsPage } from 'apps/commerce/types.ts'
import { useCart } from 'apps/vtex/hooks/useCart.ts'
import OutOfStock from 'site/components/product/OutOfStock.tsx'
import RenderHTML from 'site/components/ui/RenderHTML.tsx'
import ShippingSimulation from 'site/components/ui/ShippingSimulation.tsx'
import WishlistButton from 'site/components/wishlist/WishlistButton.tsx'
import c from 'site/sdk/c.ts'
import template from 'site/sdk/template.ts'
import { ToAddToWishlist } from 'site/sdk/ga4/events/add_to_wishlist.ts'
import { toProductItem } from 'site/sdk/ga4/transform/toProductItem.ts'
import { groupBy } from 'std/collections/group_by.ts'
import _BrowserLog from 'site/islands/BrowserLog.tsx'

import { Props as ProductDetailsProps } from 'site/components/product/ProductDetails.tsx'
import Similars from 'site/components/product/ProductSimilars.tsx'
import _ProductAppPrice, {
    ProductAppPriceAdminProps,
} from 'site/components/product/ProductAppPrice.tsx'

const hasKitlook = signal(false)
export const productSelectedSkuID = signal<string | null>(null)

function ProductInfo(
    {
        page,
        similars,
        selectedSkuId,
        seals,
        timers,
        texts,
        paymentMethods: _paymentMethods,
        paymentMethodsButtonText,
        similarsVariantions,
        pixDiscount,
        hidePixDiscountInProduct,
        renderOnlyShipping,
        shippingVariant,
        appPrice: _appPrice,
        descriptionVariant,
        campaignStudio,
    }: {
        href: string
        page: ProductDetailsPage
        similars: SimilarProduct[]
        selectedSkuId: string | null
        seals: SealConfig[]
        timers: TimerConfig[]
        texts: ProductDetailsProps['texts']
        paymentMethods: ProductDetailsProps['paymentMethods']
        paymentMethodsButtonText: ProductDetailsProps['paymentMethodsButtonText']
        pixDiscount?: number
        hidePixDiscountInProduct?: boolean
        similarsVariantions?: boolean
        renderOnlyShipping?: boolean
        shippingVariant?: 'open' | 'closed'
        appPrice: ProductAppPriceAdminProps
        descriptionVariant?: 'default' | 'alt'
        campaignStudio?: 'ativo' | 'desativo'
    },
) {
    const { breadcrumbList, product } = page

    const categoryLink =
    breadcrumbList.itemListElement[
      breadcrumbList.itemListElement.length - 2
    ]?.item ?? '/'

    const {
        productID,
        offers,
        name,
        gtin: gtinProduct,
        isVariantOf,
        sku: skuProduct,
        description,
        additionalProperty = [],
        isAccessoryOrSparePartFor,
    } = product
    const { productGroupID, hasVariant = [] } = isVariantOf ?? {}
    const { cart } = useCart()
    const currency = cart.value?.storePreferencesData?.currencyCode ?? 'BRL'
    const skus = isVariantOf?.hasVariant?.map((variant) => variant.sku) ?? []
    const variant = productSelectedSkuID.value && isVariantOf?.hasVariant
        ? isVariantOf.hasVariant.find((variant) => variant.sku === productSelectedSkuID.value)
        : null
    // If no variant is selected, displays the offer of the cheapest
    const offersToDisplay = !productSelectedSkuID.value && isVariantOf
        ? getCheapestVariantOffers(isVariantOf.hasVariant)
        : variant
        ? variant?.offers
        : offers
    const gtin = variant ? variant.gtin : gtinProduct
    const sku = variant ? variant.sku : skuProduct
    const isAvailableProduct = offers?.offers?.[0]?.availability === 'https://schema.org/InStock'

    const isAvailableVariant = hasVariant
        ? variant?.offers?.offers?.[0]?.availability === 'https://schema.org/InStock'
        : isAvailableProduct

    const {
        price = 0,
        listPrice,
        seller = '1',
        installments: paymentMethods,
        bestInstallment,
        availability,
    } = useOffer(isAvailableProduct ? offersToDisplay : offers)
    const paymentsGroup = paymentMethods ? groupBy(paymentMethods, (i) => i.name as string) : {}
    const installmentNames = {
        'Cartão Torra': 'Torra',
        Mastercard: 'Outros',
        Vale: 'Boleto',
        Pix: 'Pix',
        AmeDigital: 'AmeDigital',
        PicPay: 'PicPay',
    } as const
    const installments = Object.entries(installmentNames).reduce((acc, [name, betterName]) => {
        acc[betterName] = (paymentsGroup[name] ?? [])
            .map((i) => {
                if (i.billingIncrement && i.billingDuration) {
                    const pixValue = pixDiscount ?? 3
                    const finalPrice = i.name === 'Pix'
                        ? (i.billingIncrement * (1 - pixValue / 100))
                        : i.billingIncrement
                    return {
                        price: finalPrice,
                        billingDuration: i.billingDuration,
                    }
                }
            })
            .filter((i): i is {
                price: number
                billingDuration: number
            } => !!i)
        return acc
    }, {} as Record<string, {
        price: number
        billingDuration: number
    }[]>)
    const isAvailable = availability === 'https://schema.org/InStock'
    const { filteredSeals } = useSeals({ seals, product })
    const pdpSeals = filteredSeals.filter(({ showInPDP }) => showInPDP)
    const hasDiscount = !!price && price > 0 && !!listPrice && listPrice > price
    const isKitLook = additionalProperty.find((property) => property.propertyID === '2268')

    const dataAtual = new Date()
    const datavigenteInicio = texts.miniBannerPDP?.startAt
        ? new Date(texts.miniBannerPDP.startAt)
        : null
    const datavigenteFim = texts.miniBannerPDP?.expiresAt
        ? new Date(texts.miniBannerPDP.expiresAt)
        : null
    // if (dataAtual >= datavigenteInicio && dataAtual <= datavigenteFim) {
    //   console.log("Mini banner disponível");
    // } else {
    //   console.log("Mini banner não disponível");
    // }
    const appsflyerLink =
        product.isVariantOf?.additionalProperty.find((property) =>
            property.name === 'link appsflyer'
        )?.value || ''

    const isHidePixDiscount = pixDiscount === 0 || hidePixDiscountInProduct

    const isModoReduzido = texts.outOfStockTexts?.modoReduzido === 'Ligado'
    const mostrarModoReduzido = isModoReduzido && !isAvailable

    if (similars) {
        hasKitlook.value = true
    }
    const wishlist_analytics: ToAddToWishlist = {
        items: [toProductItem(product, { index: 0, quantity: 1 })],
        view: { id: 'product_details', name: 'Página de Produto', pageType: 'PDP' },
    }

    const similarCurrentProduct = similarsVariantions && similars
        ? {
            isAvailable,
            color: product.isVariantOf?.additionalProperty.find((property) =>
                property.name === 'Cor'
            )?.value,
            imageUrl: product && product.image ? product.image[0].url : '',
            name: product.name,
            productId: product.productID,
            url: product.url,
        } as unknown as SimilarProduct
        : undefined

    const selectedVariant = hasVariant?.find((variant) =>
        variant.sku === productSelectedSkuID.value
    )
    const selectedVariantName = selectedVariant?.name || product.name
    console.log({ bestInstallment })

    return (
        <>
            <PDPTimer timers={timers} />

            <div class='flex items-center'>
                {/* Seal */}
                {pdpSeals.length
                    ? (
                        <ul class='flex gap-x-1.5 items-center'>
                            {pdpSeals.map(({ label, image, bgColor, color, size }) => {
                                const s = size?.split('x')
                                return (
                                    <li
                                        class={c(
                                            'grid place-content-center text-xs px-2 font-bold leading-none max-w-[200px]',
                                            !image && 'py-1.5 px-2.5 max-w-fit',
                                        )}
                                        style={{
                                            backgroundColor: bgColor ?? '#ffffff',
                                            color: color ?? '#171843',
                                            // backgroundImage: `url(${image})`,
                                            backgroundRepeat: 'no-repeat',
                                            backgroundSize: '100% 100%',
                                            width: image && s?.[0] && `${s[0]}px`,
                                            height: image && s?.[1] && `${s[1]}px`,
                                        }}
                                    >
                                        <span>
                                            {image ? <img src={image} alt='' /> : label}
                                        </span>
                                    </li>
                                )
                            })}
                        </ul>
                    )
                    : (
                        <h1 class='mb-1' id='product-name'>
                            <span class='text-2xl text-neutral-0 font-bold'>
                                {isVariantOf?.name ?? name}
                            </span>
                        </h1>
                    )}
                <div class='ml-auto'>
                    <WishlistButton
                        productGroupID={productGroupID}
                        productID={productID}
                        analytics={wishlist_analytics}
                    />
                </div>
            </div>

            {/* Code and name */}
            <div class='mt-4 mb-3 sm:mt-0'>
                {!!pdpSeals.length && (
                    <h1 class='mb-1' id='product-name'>
                        <span class='text-2xl text-neutral-0 font-bold'>
                            {isVariantOf?.name ?? name}
                        </span>
                    </h1>
                )}
                <div class='flex flex-col'>
                    {gtin && (
                        <RenderHTML
                            class='text-neutral-2 [&_strong]:font-bold text-sm'
                            html={texts.productCodeText.replace('{{code}}', gtin)}
                        />
                    )}

                    {false && (
                      <div
                        id='yv-review-quickreview'
                        value={productGroupID}
                        style={{ height: '23px', cursor: 'pointer' }}
                      />
                    )}
                </div>
            </div>

            {/* Prices */}
            {!mostrarModoReduzido && !!price && price > 0 && (
                <>
                    <div class='flex flex-col gap-y-2 mb-2'>
                        {hasDiscount && (
                            <RenderHTML
                                html={template(texts.priceWithoutDiscountText, {
                                    price: formatPrice(listPrice),
                                })}
                                class='line-through text-neutral-2 text-sm'
                            />
                        )}
                        <span class='text-primary-700 text-2xl font-extrabold'>
                            {hasDiscount
                                ? (
                                    <RenderHTML
                                        html={template(texts.priceWithDiscountText, {
                                            price: formatPrice(price),
                                        })}
                                    />
                                )
                                : (
                                    <RenderHTML
                                        html={template(texts.priceIfDontHaveDiscountText, {
                                            price: formatPrice(price),
                                        })}
                                    />
                                )}
                        </span>
                    </div>
                    {/* Best installment */}
                    {bestInstallment?.billingIncrement &&
                        bestInstallment.billingDuration &&
                        bestInstallment.billingDuration >= 1 && (
                        <div class='flex gap-x-1 items-center mb-1.5'>
                            <Icon id='ProductCard' width={20} height={15} />
                            <RenderHTML
                                class='text-neutral-1 [&_strong]:font-bold text-sm'
                                html={texts.installmentText
                                    .replace(
                                        '{{duration}}',
                                        bestInstallment.billingDuration.toString(),
                                    )
                                    .replace(
                                        '{{price}}',
                                        formatPrice(bestInstallment.billingIncrement),
                                    )}
                            />
                            {/* Payment Methods */}
                            {price && paymentMethods && (
                                <div class='ml-auto'>
                                    <label
                                        for='product-payment-methods'
                                        class='text-primary-700 underline underline-offset-2 flex items-center gap-x-1 cursor-pointer text-xs'
                                    >
                                        {paymentMethodsButtonText}
                                        <Icon
                                            id='PaymentMethodsChevron'
                                            height={6}
                                            width={12}
                                            class='translate-y-0.5'
                                        />
                                    </label>
                                    <PaymentMethods
                                        visuals={_paymentMethods.visuals}
                                        modalTitle={_paymentMethods.modalTitle}
                                        id='product-payment-methods'
                                        currency={currency}
                                        installments={installments}
                                        pixDiscount={pixDiscount}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {
                        /*<ProductAppPrice
                        {...appPrice}
                        link={appsflyerLink}
                        originalPrice={price}
                        skudId={sku}
                    />*/
                    }

                    {texts.miniBannerPDP && datavigenteInicio && datavigenteFim &&
                        (dataAtual >= datavigenteInicio && dataAtual <= datavigenteFim) && (
                        <div className='flex gap-x-1 items-center mb-1.5 mt-3'>
                            <a
                                href={appsflyerLink ? appsflyerLink : texts.miniBannerPDP.urlText}
                                target='_blank'
                                rel='noopener noreferrer' // For security
                                className=''
                            >
                                <img
                                    src={texts.miniBannerPDP.imageSrc}
                                    alt={texts.miniBannerPDP.title}
                                    width={210}
                                    height={50}
                                    className=''
                                />
                            </a>
                        </div>
                    )}

                    {/* Pix discount */}
                    {
                        /*{price && (
                        <RenderHTML html={texts.pixDiscountText} class='text-sm text-neutral-1' />
                    )}*/
                    }

                    {(!isHidePixDiscount) && (
                        <div class='text-neutral-1 text-sm'>
                            <span>ou</span> <strong>{pixDiscount}%</strong>{' '}
                            <strong>de desconto no Pix</strong>
                        </div>
                    )}
                </>
            )}

            {/* Torra Campaign Studio */}
            {campaignStudio === 'ativo' ? (
              <div data-torra-stories-pdp></div>
            ) : null}

            {isKitLook && isAccessoryOrSparePartFor
                ? (
                    <KitLookDetails
                        parts={isAccessoryOrSparePartFor}
                        name={isVariantOf?.name ?? name ?? ''}
                        gtin={gtin}
                        layoutVariant='details'
                    />
                )
                : (
                    <>
                        {/* Other colors */}
                        <Similars
                            similars={similars}
                            texts={texts}
                            layout={similarsVariantions ? 'productDetailsVariant' : undefined}
                            currentProduct={similarCurrentProduct}
                        />

                        {/* Sku Selector */}
                        {/* {!mostrarModoReduzido && ( */}

                        <div class='mt-4 mb-4 sm:mb-6'>
                          <ProductDetailsVariantSelector
                            product={product}
                            skusText={texts.skusText}
                          />
                        </div>

                        {/* )} */}

                        {!isAvailable && (
                          <div class='mt-4'>
                            <OutOfStock productID={productID} texts={texts.outOfStockTexts} categoryLink={categoryLink} />
                          </div>
                        )}

                        {/* Container do Widget Reverbs */}
              <button data-reverbs-trigger data-reverbs-auto-show style='display:none' class='bt-proveiai py-2 px-4 text-white bg-primary-700 rounded-md text-base font-bold mb-3 transition delay-150 duration-300 ease-in-out hover:-translate-y-1 hover:bg-primary-500'>
                          <span style="margin-right:8px;display:inline-flex;vertical-align:middle"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"></path><path d="M20 3v4"></path><path d="M22 5h-4"></path><path d="M4 17v2"></path><path d="M5 18H3"></path></svg></span>
                          PROVE COM I.A.
                        </button>


                        {/* Add to Cart */}
                        {isAvailable && (
                            <ProductDetailsAddToCart
                                variant='addToCart'
                                //selectedSkuId={selectedSkuId}
                                isAvailable={isAvailableVariant}
                                texts={texts}
                                categoryLink={categoryLink}
                                itemsToAdd={[
                                    {
                                        brand: product.brand,
                                        category: product.category ?? '',
                                        breadcrumbListItems: breadcrumbList.itemListElement.slice(
                                            0,
                                            -1,
                                        ),
                                        productGroupID: product.isVariantOf?.productGroupID,
                                        productGroupName: product.isVariantOf?.name,
                                        listPrice: listPrice ?? price ?? 0,
                                        productID: productID,
                                        url: product.url ?? '',
                                        skuId: productID,
                                        sellerId: seller,
                                        price: price ?? 0,
                                        discount: price && listPrice ? listPrice - price : 0,
                                        name: selectedVariantName ?? '',
                                        productGroupId: product.isVariantOf?.productGroupID ?? '',
                                    },
                                ]}
                            />
                        )}

                        <div id='doris-trigger-wrapper' data-skus={skus.join(',')}></div>

                        {isAvailable && (
                            <ShippingSimulation
                                items={[
                                    {
                                        id: Number(sku),
                                        quantity: 1,
                                        seller: seller,
                                    },
                                ]}
                                texts={texts.shippingTexts}
                                renderOnlyShipping={renderOnlyShipping}
                                variant={shippingVariant}
                            />
                        )}

                        <div class='mt-5'>
                            {description && (
                                <Description text={description} variant={descriptionVariant} />
                            )}
                        </div>
                    </>
                )}

            <SendEventOnLoad
                event={{
                    name: 'view_item',
                    params: {
                        currency: offers?.priceCurrency ?? 'BRL',
                        value: price,
                        items: [
                            mapProductToAnalyticsItem({
                                product: {
                                    ...hasVariant[0],
                                    isVariantOf,
                                },
                                breadcrumbList: {
                                    ...breadcrumbList,
                                    itemListElement: breadcrumbList.itemListElement.slice(0, -1),
                                },
                                index: 0,
                                price,
                                listPrice,
                            }),
                        ],
                    },
                }}
            />
        </>
    )
}

export default ProductInfo

// Arquivo de interesse para o rollback da demanda #4561
// Este é um dos arquivos que foram (ou que poderão ser) alterados 
// para realização do rollback